import {Injectable} from '@angular/core';
import {Action, Store} from '@ngrx/store';
import {
  catchError,
  map,
  mergeMap,
  Observable,
  of,
  pluck,
  take,
  tap
} from 'rxjs';
import * as fromRoot from '../../../../state/app.state';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  DeleteResourceFailure,
  DeleteResourceRequest,
  DeleteResourceSuccess,
  FetchCategoriesListFailure,
  FetchCategoriesListRequest,
  FetchCategoriesListResourceListFailure,
  FetchCategoriesListResourceListRequest,
  FetchCategoriesListResourceListSuccess,
  FetchCategoriesListSuccess,
  FetchRelatedResourcesFailure,
  FetchRelatedResourcesRequest,
  FetchRelatedResourcesSuccess,
  FetchResourceDetailsForEditFailure,
  FetchResourceDetailsForEditRequest,
  FetchResourceDetailsForEditSuccess,
  FetchResourceListFailure,
  FetchResourceListRequest,
  FetchResourceListSuccess,
  FetchResourceStatusFailure,
  FetchResourceStatusRequest,
  FetchResourceStatusSuccess,
  FetchRolesListFailure,
  FetchRolesListRequest,
  FetchRolesListResourceListFailure,
  FetchRolesListResourceListRequest,
  FetchRolesListResourceListSuccess,
  FetchRolesListSuccess,
  FetchSessionListFailure,
  FetchSessionListRequest,
  FetchSessionListResourceListFailure,
  FetchSessionListResourceListRequest,
  FetchSessionListResourceListSuccess,
  FetchSessionListSuccess,
  FetchSubCategoriesListFailure,
  FetchSubCategoriesListRequest,
  FetchSubCategoriesListResourceListFailure,
  FetchSubCategoriesListResourceListRequest,
  FetchSubCategoriesListResourceListSuccess,
  FetchSubCategoriesListSuccess,
  GetResourceDetailFailure,
  GetResourceDetailRequest,
  GetResourceDetailSuccess,
  SaveOrPublishResourceFailure,
  SaveOrPublishResourceRequest,
  SaveOrPublishResourceSuccess,
  FetchOrganisationsListRequest,
  FetchOrganisationsListSuccess,
  FetchOrganisationsListFailure,
  FetchGroupsListRequest,
  FetchGroupsListSuccess,
  FetchGroupsListFailure
} from './resources-hub.actions';
import {ResourcesHubService} from './resources-hub.service';
import {
  DeleteResourcesResponse,
  RoleListResponse,
  SubCategoriesList
} from '../model/resource-details.model';
import {CreateResourceStatus} from "../constants/create-resource-status.enum";
import {CommonService} from "../../../shared/services/common.service";
import {
  getCategoriesList,
  getRelatedResources
} from './resources-hub.selectors';
import {Router} from "@angular/router";


@Injectable()
export class ResourcesHubEffects {
  fetchResourceDetails$: Observable<Action> =
    createEffect(() => this.actions.pipe(ofType(GetResourceDetailRequest), map(actions => actions.payload), mergeMap((payload: {
      resourceId: string,
      slug: string
    }) => {
      this.commonService.startLoading();
      return this.resourceHubService.getResourceDetails({id: payload.resourceId, slug: payload.slug})
        .pipe(map((resourceDetails: any) => {
          const categoriesList = []
          resourceDetails.data.categories.forEach(category => {
            categoriesList.push({
              id: category.id,
              name: category.value,
              type: 'main'
            })
            category.subCategories.forEach((subCategory) => {
              categoriesList.push({
                id: subCategory.id,
                name: subCategory.value,
                type: 'sub'
              })
            })
          });
          return GetResourceDetailSuccess({data: {...resourceDetails.data, categories: categoriesList}});
        }), catchError((err) => {
          return of(GetResourceDetailFailure({error: err}));
        }), tap((action: any) => {
          if (action.type === GetResourceDetailSuccess.type) {
            // Code to execute on API Success Action dispatch
          } else if (action.type === GetResourceDetailFailure.type) {
            // Code to execute on API Failure Action dispatch
          }
          this.commonService.stopLoading();
        }))
    })))

  fetchSessionList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchSessionListRequest), mergeMap((payload: any) => {
    this.commonService.startLoading();
    return this.resourceHubService.fetchSessionList()
      .pipe(map(sessionResponse => {
        return FetchSessionListSuccess({sessionList: sessionResponse.data})
      }), catchError((err: any) => of(FetchSessionListFailure({error: err}))), tap((action: any) => {
        if (action.type === FetchSessionListSuccess.type) {
          //api success call
        } else if (action.type === FetchSessionListFailure.type) {
          //api success call
        }
        this.commonService.stopLoading();
      }))
  })))

  fetchRolesList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchRolesListRequest), mergeMap((payload: any) => {
    return this.resourceHubService.fetchRoleList()
      .pipe(map((roleList: RoleListResponse['response']) => {
        return FetchRolesListSuccess({roleList: roleList.data})
      }), catchError((err: any) => of(FetchRolesListFailure({error: err}))), tap((action: any) => {
        if (action.type === FetchRolesListSuccess.type) {
          //api success call
        } else if (action.type === FetchRolesListFailure.type) {
          //api success call
        }
      }))
  })))

  fetchCategoriesList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchCategoriesListRequest), map(action => action), mergeMap(payload => this.resourceHubService.fetchCategoriesList()
    .pipe(map(categories => {
      return FetchCategoriesListSuccess({categories: categories.data})
    }), catchError(err => of(FetchCategoriesListFailure())), tap((action: any) => {
      if (action.type === FetchCategoriesListSuccess.type) {
        //api success call
      } else if (action.type === FetchCategoriesListFailure.type) {
        //api failure call
      }
    }))
  )))

  fetchSubCategoriesList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchSubCategoriesListRequest), map(action => {
    return action.payload
  }), mergeMap(payload => {
    const requestPayload: SubCategoriesList['request'] = {
      categories: [payload.categoryId]
    }
    return this.resourceHubService.fetchSubCategoriesList(requestPayload)
      .pipe(mergeMap(response => this.store.select(getCategoriesList)
        .pipe(take(1), mergeMap(categoriesData => of({categoriesData, response})))), map((categories: any) => {
        const {categoriesData, response} = categories;
        let categoriesUpdatedData = [...categoriesData]
        const updatedData = categoriesUpdatedData.map(category => category.key === payload.categoryId ? {
          ...category,
          subCategories: response.data,
          loading: false
        } : {...category});
        return FetchSubCategoriesListSuccess({subCategoriesList: updatedData})
      }), catchError(err => of(FetchSubCategoriesListFailure())), tap((action: any) => {
        if (action.type === FetchSubCategoriesListSuccess.type) {
          //api success call
        } else if (action.type === FetchSubCategoriesListFailure.type) {
          //api failure call
        }
      }))
  })))

  fetchRelatedResource$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchRelatedResourcesRequest), map(action => action.payload), mergeMap(payload => {
    return this.resourceHubService.fetchRelatedResources(payload)
      .pipe( map((response: any) => {
        return FetchRelatedResourcesSuccess({resources: {...response.data}})
      }), catchError(err => of(FetchRelatedResourcesFailure())), tap((action: any) => {
        if (action.type === FetchRelatedResourcesSuccess.type) {
          //api success call
        } else if (action.type === FetchRelatedResourcesFailure.type) {
          //api failure call
        }
      }))
  })))

  publishResource$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(SaveOrPublishResourceRequest), map(actions => actions.payload), mergeMap((payload: any) => {
    this.commonService.startLoading();
    const categoriesData = payload?.resource?.categories.map(categories => {
      let subCategoriesData = []
      if (categories.subCategories.length > 0) {
        subCategoriesData = categories?.subCategories.map(subCategories => subCategories)
      }
      return {id: categories.name, subCategories: subCategoriesData}
    })
    const relatedResource = payload.resource.relatedResources.map(resource => resource.id)
    const organisations = (payload.resource.organisations || []).map(o => o.key || o.id || o);
    const groups = (payload.resource.groups || []).map(g => g.key || g.id || g);
    const updatedPayload = {
      ...payload.resource,
      categories: categoriesData,
      relatedResources: relatedResource,
      organisations,
      groups,
    }
    delete updatedPayload.statusName
    delete updatedPayload.isActive
    delete updatedPayload.publishOn
    delete updatedPayload.slug
    return this.resourceHubService.createResource(updatedPayload).pipe(
      map((resourceCreated: any) => {
        if (updatedPayload.status === CreateResourceStatus.draft) {
          this.resourceHubService.setSaveAsDraft(true);
        }
        const categoryData = resourceCreated.data.categories.map((categories: any) => {
          const subcategories = categories.subCategories.map(sub => sub.id)
          return {name: categories.id, subCategories: subcategories}
        })
        const sessions = resourceCreated.data.sessions.map(session => session.id)
        const organisations = (resourceCreated.data.organisations || []).map(o => o.key || o.id || o)
        const groups = (resourceCreated.data.groups || []).map(g => g.key || g.id || g)
        const updatedResourceData = {...resourceCreated.data}
        return SaveOrPublishResourceSuccess({
          payload: {
            resource: {...updatedResourceData, categories: categoryData, sessions: sessions, organisations, groups},
            message: resourceCreated.message
          }
        })
      }), catchError(err => of(SaveOrPublishResourceFailure())), tap((action: any) => {
        if (action.type === SaveOrPublishResourceSuccess.type) {
          if (action.payload.resource.status === CreateResourceStatus.published) {
            const urlPrefix = this.router.url.split('/')[1];
            this.router.navigate([`/${urlPrefix}/resource-hub/resource-details/${action.payload.resource.id}/${action.payload.resource.slug}`])

          }
          this.commonService.notification(action.payload.message, 'success');
        } else if (action.type === SaveOrPublishResourceFailure.type) {
          //api failure call
        }
        this.commonService.stopLoading();
      })
    )
  })))

  fetchEditResourceRequest$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchResourceDetailsForEditRequest), map(actions => actions.payload), mergeMap(payload => {
    // this.commonService.startLoading();
    return this.resourceHubService.getResourceDetails({id: payload.resourceId, slug: payload.slug})
      .pipe(map(resourceDetails => {
        const categoryData = resourceDetails.data.categories.map((categories: any) => {
          const subcategories = categories.subCategories.map(sub => sub.id)
          return {name: categories.id, subCategories: subcategories}
        })
        const sessions = resourceDetails.data.sessions.map(session => session.id)
        const organisations = (resourceDetails.data.organisations || []).map(o => o.key || o.id || o)
        const groups = (resourceDetails.data.groups || []).map(g => g.key || g.id || g)
        const updatedResourceData = {...resourceDetails.data}
        return FetchResourceDetailsForEditSuccess({resourceDetails: {...updatedResourceData, categories: categoryData, sessions: sessions, organisations, groups}})
      }), catchError(err => of(FetchResourceDetailsForEditFailure())), tap((action: any) => {
        if (action.type === FetchResourceDetailsForEditSuccess.type) {
          //success call
        } else if (action.type === FetchResourceDetailsForEditFailure.type) {
          //failure call
        }
        // this.commonService.stopLoading();
      }))
  })))

  deleteResource$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(DeleteResourceRequest), map(actions => actions.payload), mergeMap(payload => {
    this.commonService.startLoading();
    const updatedPayload: DeleteResourcesResponse['request'] = {
      id: payload.resourceId,
      status: (CreateResourceStatus.deleted as number)
    }
    return this.resourceHubService.deleteResource(updatedPayload)
      .pipe(map((resourceDelete: any) => DeleteResourceSuccess({payload: {message: resourceDelete.message}})), catchError(err => of(DeleteResourceFailure())), tap((action: any) => {
        if (action.type === DeleteResourceSuccess.type) {
          this.commonService.notification(action.payload.message, 'success')
          const urlPrefix = this.router.url.split('/')[1];
          this.router.navigate([`/${urlPrefix}/resource-hub/resources-list`])
          //success call
        } else if (action.type === DeleteResourceFailure.type) {
          //failure call
        }
        this.commonService.stopLoading();
      }))
  })))

  fetchSessionListResourceList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchSessionListResourceListRequest), mergeMap((payload: any) => {
    return this.resourceHubService.fetchSessionList()
      .pipe(map(sessionResponse => {
        return FetchSessionListResourceListSuccess({
          payload: {sessionList: sessionResponse.data}
        })
      }), catchError((err: any) => of(FetchSessionListResourceListFailure())), tap((action: any) => {
        if (action.type === FetchSessionListResourceListSuccess.type) {
          //api success call
        } else if (action.type === FetchSessionListResourceListFailure.type) {
          //api success call
        }
      }))
  })))

  fetchRolesListResourceList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchRolesListResourceListRequest), mergeMap((payload: any) => {
    return this.resourceHubService.fetchRoleList()
      .pipe(map((roleList: RoleListResponse['response']) => {
          return FetchRolesListResourceListSuccess({
            payload: {roleList: roleList.data}
          })
        }), catchError((err: any) => of(FetchRolesListResourceListFailure())),
        tap((action: any) => {
          if (action.type === FetchRolesListResourceListSuccess.type) {
            //api success call
          } else if (action.type === FetchRolesListResourceListFailure.type) {
            //api success call
          }
        }))
  })))

  fetchCategoriesListResourceList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchCategoriesListResourceListRequest), map(action => action), mergeMap(payload => this.resourceHubService.fetchCategoriesList()
    .pipe(map(categories => {
        return FetchCategoriesListResourceListSuccess({
          payload: {categories: categories.data}
        })
      }), catchError(err => of(FetchCategoriesListResourceListFailure())),
      tap((action: any) => {
        if (action.type === FetchCategoriesListResourceListSuccess.type) {
          //api success call
        } else if (action.type === FetchCategoriesListResourceListFailure.type) {
          //api failure call
        }
      }))
  )))

  fetchSubCategoriesListResourceList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchSubCategoriesListResourceListRequest), map(action => {
    return action.payload
  }), mergeMap(payload => {
    const requestPayload: SubCategoriesList['request'] = {
      categories: [...payload.categories]
    }
    return this.resourceHubService.fetchSubCategoriesList(requestPayload)
      .pipe(map((categories) => {
          return FetchSubCategoriesListResourceListSuccess({
            payload: {
              subCategories: categories.data
            }
          })
        }), catchError(err => of(FetchSubCategoriesListResourceListFailure())),
        tap((action: any) => {
          if (action.type === FetchSubCategoriesListResourceListSuccess.type) {
            //api success call
          } else if (action.type === FetchSubCategoriesListResourceListFailure.type) {
            //api failure call
          }
        }))
  })))

  fetchResourceList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchResourceListRequest),
      map(actions => {
        return {
          sortColumn: "createdAt",
          sortOrder: -1,
          ...actions.payload
        }
      }),
      mergeMap((payload: any) => this.resourceHubService.fetchResourceList(payload)
        .pipe(
          map(response => {
            const {data} = response;
            return FetchResourceListSuccess({data})
          }),
          catchError(err => of(FetchResourceListFailure())),
          tap((action: any) => {
            if (action.type === FetchResourceListSuccess.type) {
              //api success call
            } else if (action.type === FetchResourceListFailure.type) {
              //api failure call
            }
          })
        )
      )
    )
  );

  fetchResourceStatusList$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchResourceStatusRequest), map(actions => actions), mergeMap(payload => this.resourceHubService.fetchResourceStatusList()
    .pipe(
      map((response: any) => {
          return FetchResourceStatusSuccess({payload: {statusList: response.data.list}})
        }
      ),
      catchError(err => of(FetchResourceStatusFailure())),
      tap((action: any) => {
        if (action.type === FetchResourceStatusSuccess.type) {
          //api success call
        } else if (action.type === FetchResourceStatusFailure.type) {
          //api failure call
        }
      })))))

  fetchOrganisationsList$: Observable<Action> = createEffect(() => this.actions.pipe(
    ofType(FetchOrganisationsListRequest),
    mergeMap(() => {
      return this.resourceHubService.fetchOrganisationsList()
        .pipe(
          map((response: any) => {
            const list = response?.data?.list?.map(item => ({key: item.key || item.id, value: item.value || item.name})) || [];
            return FetchOrganisationsListSuccess({organisationsList: list});
          }),
          catchError(() => of(FetchOrganisationsListFailure()))
        );
    })
  ))

  fetchGroupsList$: Observable<Action> = createEffect(() => this.actions.pipe(
    ofType(FetchGroupsListRequest),
    mergeMap((action: any) => {
      return this.resourceHubService.fetchGroupsList(action.payload || {})
        .pipe(
          map((response: any) => {
            const list = response?.data?.list?.map(item => ({key: item.key || item.id, value: item.value || item.name})) || [];
            return FetchGroupsListSuccess({groupsList: list});
          }),
          catchError(() => of(FetchGroupsListFailure()))
        );
    })
  ))

  constructor(private store: Store<fromRoot.State>, private actions: Actions, private resourceHubService: ResourcesHubService, private commonService: CommonService, private router: Router) {
  }
}
